import * as IkeModel from '@ikegps/core-shared';
import { IkeGeodesic } from '@ikegps/core-shared';
import { CollectionTitle } from '../../../components/collection/collection.title';
import { Input } from '../../../components/input/input';
import { Spinner } from '../../../components/spinner/spinner';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n/index';
import { m, type MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';
import { CurrentState } from '../../../state/page.state';
import { AppStore } from '../../../store';
import { CaptureThumbnail, type CaptureThumbnailCtx } from '../../capture.thumbnail/capture.thumbnail';
import { FieldInfo } from '../../field.info/field.info';
import type { ICollectionViewController } from '../collection.view.ctrl.interface';
import type { CollectionFieldView } from './field.view';

export const CSS = {
	capture: 'c-Vector__capture',
};

addStyles({
	[CSS.capture]: {
		padding: DefaultTheme.space(1),
	},
});

function viewTopLevelCaptures(collection: IkeModel.CollectionModel): MithrilVNode[] {
	const imageCtx: CaptureThumbnailCtx = { truesize: true, className: CSS.capture };

	const nodes: MithrilVNode[] = [];
	for (const formField of collection.schema.fields) {
		const field = collection.getField(formField.id);
		if (!IkeModel.CollectionModelFieldTrueSize.isTrueSizeField(field) || !field.hasValue()) {
			continue;
		}

		const captures = field.getCaptures();
		for (const capture of captures) {
			imageCtx.title = Services.DateTime.formatDateTime(capture.collectedAt.iso);
			nodes.push(CaptureThumbnail.view(collection, capture, imageCtx));
		}
	}

	return nodes;
}

export const VectorCollectionFieldView: CollectionFieldView<IkeModel.CollectionModelFieldVector> = {
	viewInline(field: IkeModel.CollectionModelFieldVector, ctrl: ICollectionViewController): MithrilVNode | string {
		const targetCollections = field.getCollections();
		if (targetCollections == null || targetCollections[0] == null) {
			return m('span', Translate(T.UNKNOWN));
		}

		const targetCollection =
			AppStore.CollectionSummary.getLatest(targetCollections[0].revision) || targetCollections[0];

		let titleNode: MithrilVNode | string;
		let vectorValue = field.value;
		if (ctrl.canInspectCollection(targetCollection)) {
			titleNode = m(
				'a',
				{
					title: Translate(T.COLLECTION_EDIT),
					onclick: () => {
						Services.Analytics.trackEvent(
							Services.Analytics.EventCategory.Mutate,
							'vector:link:navigate',
							field.schema.displayName,
							1
						);
						ctrl.inspectCollection(targetCollection.revision);
					},
				},
				CollectionTitle.view(targetCollection.title)
			);
			const targetCollectionModel = AppStore.Collection.get(targetCollection.revision);
			if (targetCollectionModel && targetCollectionModel.location) {
				vectorValue = IkeGeodesic.getVector(field.collection.location, targetCollectionModel.location);
			}
		} else {
			titleNode = CollectionTitle.view(targetCollection.title);
		}
		const onmouseover = () => {
			CurrentState.setHoverId(field.id);
		};
		const onmouseout = () => {
			CurrentState.setHoverId(null);
		};
		const className = Input.CSS.container;
		return m('div', { className, onmouseover, onmouseout }, [
			m('span', [FieldInfo.viewVectorInline(vectorValue), ' - ', titleNode]),
		]);
	},

	hasDetail(field: IkeModel.CollectionModelFieldVector) {
		return true;
	},

	viewDetail(field: IkeModel.CollectionModelFieldVector): MithrilVNode {
		const targetCollections = field.getCollections();
		if (targetCollections == null || targetCollections[0] == null) {
			return null;
		}

		const targetCollection = targetCollections[0];
		const targetCollectionModel = AppStore.Collection.get(targetCollection.revision);
		if (targetCollectionModel == null) {
			return Spinner.view();
		}

		const captureNodes = viewTopLevelCaptures(targetCollectionModel);
		if (captureNodes.length === 0) {
			return m('span', Translate(T.NO_CAPTURES));
		}

		return m.fragment({}, captureNodes);
	},

	viewEdit(field: IkeModel.CollectionModelFieldVector, ctrl: ICollectionViewController): MithrilVNode | string {
		return VectorCollectionFieldView.viewInline(field, ctrl);
	},
};
